import { NO_QUEUE_ESTIMATE } from "odyssey-shared";
import { say } from "../../words.ts";

/**
 * An estimated wait for the login queue in coarse words (ADR-127 decision 7), one function for every place a page
 * says it: the queue window, from a `queue` frame's `etaMs`, and the entry screens' strip, from the status's
 * `waitMs` (ADR-127 decision 8). The estimate is a rate on recent history and no promise, so it is never shown finer
 * than a minute.
 */

/**
 * The estimate's forms that are not a number of minutes.
 */
export const QUEUE_ESTIMATE_WORDING = {
  underMinute: say("queue.underMinute"),
  overHour: say("queue.overHour"),
} as const;

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

/**
 * The estimate in coarse words: under a minute; whole minutes to the nearest, from one minute to sixty; and over an
 * hour past that.
 *
 * @param etaMs The estimated wait in milliseconds, a `queue` frame's `etaMs` or the status's `waitMs`.
 * @returns The words, or null when the gateway has no estimate (-1) or the number is none the wire carries, in
 *   which case the page shows no number.
 * @example
 * queueEstimate(360_000);
 * // "About 6 min"
 */
export function queueEstimate(etaMs: number): string | null {
  if (etaMs === NO_QUEUE_ESTIMATE || !Number.isFinite(etaMs) || etaMs < 0) {
    return null;
  }
  if (etaMs < MINUTE_MS) {
    return QUEUE_ESTIMATE_WORDING.underMinute;
  }
  if (etaMs > HOUR_MS) {
    return QUEUE_ESTIMATE_WORDING.overHour;
  }
  return say("queue.aboutMinutes", { minutes: Math.round(etaMs / MINUTE_MS) });
}
